import type { ReactNode } from 'react'

import type { BatchListRow } from '@ctcells/contract'
import { t } from '@ctcells/i18n'

import type { Column } from '../../atoms/DataTable.tsx'

/**
 * 「작업자」 열 — 작업 목록과 이력이 **같은 함수**를 쓴다(두 표가 같은 값을 다르게 말하지 않게).
 *
 * 🔴 **admin 에게만 선다**(eric 2026-09-30 「그럼.. 그거 admin 만 작업자 보이게 해줘」). 응답 `Batch.created_by` 는
 *    **계정 ID** 이고, 이름은 admin 전용 `GET /users` 로만 풀린다(operator · viewer 403 · `roleAccess.ts`).
 *    그래서 화면은 `owners` 를 **받았을 때만** 이 열을 세운다 — 받을지는 컨테이너가 서버가 준 연산 목록
 *    (`/auth/me` 의 `listUsers`)으로 정한다(`useBatchOwners.tsx`). 화면은 역할을 모른다.
 * 🔴 **이름은 `GET /users` 에서만 온다. `Batch.name` 을 파싱하지 마라** — 서버가 그 안에 이름을 넣지만
 *    표시 이름이지 계약된 칸이 아니다.
 * 🔴 표에 없는 id 는 컨테이너가 `GET /users/{id}` 로 한 번 더 푼다(`renderMissing`). 그래도 못 풀면 **id 그대로**다 —
 *    지어내지 않는다. `null` 은 「만든 계정이 기록 안 됨」이라 `—` 다.
 * ⚠ 2026-09-30 오전에 「id 를 그대로 까는 열」로 섰다가 뺐다. **admin 이 아닌 표면에 id 열로 되살리지 마라.**
 */
export interface BatchOwner {
  /** `User.name` */
  name: string
  /** `User.username` — 검색에만 쓴다 */
  username: string
}

export interface BatchOwners {
  /** `user_id → 이름` — `GET /users`(활성 + `status=disabled`)를 한 번 읽은 표 */
  byId: ReadonlyMap<string, BatchOwner>
  /** 표를 아직 받는 중. 그동안 칸은 「불러오는 중」이다(id 가 먼저 번쩍이지 않게) */
  pending: boolean
  /** 표에 없는 id 의 칸. 없으면 id 그대로 */
  renderMissing?: ((userId: string) => ReactNode) | undefined
}

/**
 * 작업자 한 칸 — 목록의 열과 작업 상세 「작업」 카드의 줄이 **같은 함수**를 쓴다(두 곳이 같은 값을 다르게 말하지 않게).
 * `null` → `—` · 표에 있으면 이름 · 표를 받는 중이면 「불러오는 중」 · 없으면 `renderMissing` 또는 id 그대로.
 */
export function ownerValue(owners: BatchOwners, userId: string | null): ReactNode {
  if (userId === null) return <span className="muted">{t('mask.none')}</span>
  const owner = owners.byId.get(userId)
  if (owner !== undefined) return <span>{owner.name}</span>
  if (owners.pending) return <span className="muted">{t('state.loading')}</span>
  return owners.renderMissing?.(userId) ?? <span className="muted mono">{userId}</span>
}

export function batchOwnerColumn(owners: BatchOwners): Column<BatchListRow> {
  return {
    key: 'owner',
    header: t('column.owner'),
    nowrap: true,
    width: 'fit',
    /*
     * 🔴 **이름으로 세운다**(칸이 그리는 것) — 계정 ID 로 세우면 화면에 없는 값의 차례가 된다.
     *    표에 없는 id(못 푼 이름) · `null` 은 값 없음이라 방향 무관 맨 뒤다(`tableSort.ts`).
     */
    sort: 'text',
    sortValue: (row) => (row.created_by === null ? null : owners.byId.get(row.created_by)?.name),
    cell: (row) => ownerValue(owners, row.created_by),
  }
}

/** 검색에 거는 작업자 글자 — 이름 · 계정명. 표에 없으면 빈 배열(화면에 없는 id 로 걸지 않는다) */
export function ownerSearchTexts(
  owners: BatchOwners | undefined,
  userId: string | null,
): readonly string[] {
  if (owners === undefined || userId === null) return []
  const owner = owners.byId.get(userId)
  return owner === undefined ? [] : [owner.name, owner.username]
}
